import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { selectUserFormData } from "app/redux/selector";
import { actions as ticketFieldsActions } from "../../Tickets/TicketFields/redux/slice";
import { AssignTicketsTargetTypeEnum } from "../../Tickets/TicketFields/types";
import { backlogActions } from "../redux/slice";
import { IPlanningViewsGroup, PlanningViewBy } from "../types";

const PLANNING_DROP_VIEWS = [
  PlanningViewBy.SPRINTS,
  PlanningViewBy.COMPONENTS,
  PlanningViewBy.EPICS,
  PlanningViewBy.STORIES,
] as const;

const isPlanningDropView = (
  viewBy: PlanningViewBy,
): viewBy is (typeof PLANNING_DROP_VIEWS)[number] =>
  PLANNING_DROP_VIEWS.includes(viewBy as (typeof PLANNING_DROP_VIEWS)[number]);

export function usePlanningSprintDrop(
  viewBy: PlanningViewBy,
  group: IPlanningViewsGroup,
) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const dispatch = useDispatch();
  const { boardId } = useParams();
  const currentUser = useSelector(selectUserFormData);

  useEffect(() => {
    if (!isPlanningDropView(viewBy)) return;
    const element = cardRef.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => typeof source.data.ticketId === "string",
      onDragEnter: () => setIsDraggedOver(true),
      onDragLeave: () => setIsDraggedOver(false),
      onDrop: ({ source }) => {
        setIsDraggedOver(false);
        const { data } = source;
        if (typeof data.ticketId !== "string" || !currentUser?._id) return;

        const ticketIds =
          data.isMulti === true && Array.isArray(data.selectedTicketIds)
            ? data.selectedTicketIds.filter(
                (id): id is string => typeof id === "string",
              )
            : [data.ticketId];
        if (!ticketIds.length) return;

        const isUnassignedTarget = group.isUnassigned;
        if (!isUnassignedTarget && !group.id) return;

        const refreshPlanning = () => {
          if (!boardId) return;
          dispatch(
            backlogActions.fetchBacklogsPlanning({
              boardId: boardId as string,
              viewBy,
              skipLoading: true,
            }),
          );
        };

        if (viewBy === PlanningViewBy.SPRINTS) {
          dispatch(
            ticketFieldsActions.assignTicketsToSprintUpdate({
              data: {
                targetId: isUnassignedTarget ? "" : group.id!,
                targetType: isUnassignedTarget
                  ? AssignTicketsTargetTypeEnum.BACKLOG
                  : AssignTicketsTargetTypeEnum.SPRINT,
                ticketIds,
                userId: currentUser._id,
              },
              callback: refreshPlanning,
            }),
          );
          return;
        }

        if (viewBy === PlanningViewBy.EPICS) {
          if (isUnassignedTarget || !group.id) return;

          dispatch(
            ticketFieldsActions.assignTicketsToEpicUpdate({
              data: {
                epicId: group.id,
                ticketIds,
              },
              callback: refreshPlanning,
            }),
          );
          return;
        }

        if (viewBy === PlanningViewBy.STORIES) {
          if (isUnassignedTarget || !group.id) return;

          dispatch(
            ticketFieldsActions.assignTicketsToStoryUpdate({
              data: {
                storyId: group.id,
                ticketIds,
              },
              callback: refreshPlanning,
            }),
          );
          return;
        }

        dispatch(
          ticketFieldsActions.assignTicketsToComponentUpdate({
            data: {
              componentId: isUnassignedTarget ? "" : group.id!,
              ticketIds,
              userId: currentUser._id,
            },
            callback: refreshPlanning,
          }),
        );
      },
    });
  }, [
    viewBy,
    group.id,
    group.isUnassigned,
    dispatch,
    currentUser?._id,
    boardId,
  ]);

  const isPlanningDropTarget = isPlanningDropView(viewBy);

  return { cardRef, isDraggedOver, isPlanningDropTarget };
}
